import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { translations, Language } from "../i18n/translations";

/* @section: igc-logo */
const IGCLogo = () => (
  <div
    style={{
      backgroundColor: "#0B308E",
      display: "inline-flex",
      alignItems: "center",
      padding: "8px 14px",
    }}
  >
    <span style={{ color: "#fff", fontFamily: '"Times New Roman", Times, serif', fontSize: "1.6rem", fontWeight: "normal", lineHeight: 1, letterSpacing: "0.02em" }}>I</span>
    <span style={{ width: "1px", backgroundColor: "#fff", height: "1.3rem", margin: "0 9px", display: "block", flexShrink: 0 }} />
    <span style={{ color: "#fff", fontFamily: '"Times New Roman", Times, serif', fontSize: "1.6rem", fontWeight: "normal", lineHeight: 1, letterSpacing: "0.02em" }}>G</span>
    <span style={{ width: "1px", backgroundColor: "#fff", height: "1.3rem", margin: "0 9px", display: "block", flexShrink: 0 }} />
    <span style={{ color: "#fff", fontFamily: '"Times New Roman", Times, serif', fontSize: "1.6rem", fontWeight: "normal", lineHeight: 1, letterSpacing: "0.02em" }}>C</span>
  </div>
);

const LANG_OPTIONS: { code: Language; flag: string; label: string }[] = [
  { code: "en", flag: "🇬🇧", label: "EN" },
  { code: "fr", flag: "🇫🇷", label: "FR" },
  { code: "ar", flag: "🇸🇦", label: "AR" },
  { code: "ru", flag: "🇷🇺", label: "RU" },
  { code: "zh", flag: "🇨🇳", label: "ZH" },
];

const NAVY = "#0B308E";
const GOLD = "#C9A84C";

/* @section: language-switcher */
function LanguageSwitcher() {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const current = LANG_OPTIONS.find((o) => o.code === lang)!;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border transition-colors"
        style={{
          borderColor: open ? GOLD : "rgba(11,48,142,0.25)",
          color: NAVY,
          background: open ? `${GOLD}12` : "transparent",
          letterSpacing: "0.08em",
        }}
        aria-label="Select language"
      >
        <span>{current.flag}</span>
        <span>{current.label}</span>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-1 bg-white shadow-lg border border-border z-[200] min-w-[110px]"
          >
            {LANG_OPTIONS.map((opt) => (
              <button
                key={opt.code}
                onClick={() => { setLang(opt.code); setOpen(false); }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-medium hover:bg-primary/5 transition-colors text-left"
                style={{ color: lang === opt.code ? GOLD : NAVY, fontWeight: lang === opt.code ? 700 : 500 }}
              >
                <span>{opt.flag}</span>
                <span>{opt.label}</span>
                {lang === opt.code && (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="3" strokeLinecap="round" className="ml-auto">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* @section: navbar */
export default function Navbar() {
  const { lang, setLang } = useLang();
  const t = translations[lang];
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href: string) => {
    setOpen(false);
    const id = href.replace("#", "");
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const navLinks: { label: string; href: string; isJoin?: boolean }[] = [
    { label: t.nav_home, href: "#home" },
    { label: t.nav_about, href: "#about" },
    { label: t.nav_services, href: "#services" },
    { label: t.nav_business, href: "#business-lines" },
    { label: t.nav_team, href: "#team" },
    { label: t.nav_contact, href: "#contact" },
    { label: t.nav_join, href: "/join", isJoin: true },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 shadow-md backdrop-blur-sm" : "bg-white/80 backdrop-blur-sm"
      }`}
    >
      <nav className="container mx-auto px-6 py-3 flex items-center justify-between max-w-7xl">
        {/* Logo */}
        <button onClick={() => handleNav("#home")} className="flex-shrink-0">
          <IGCLogo />
        </button>

        {/* Desktop Links */}
        <div className="hidden md:flex items-center gap-6">
          {navLinks.map((link) => (
            link.isJoin ? (
              <button
                key={link.href}
                onClick={() => { setOpen(false); navigate(link.href); }}
                className="text-sm font-semibold px-4 py-1.5 border transition-colors"
                style={{ borderColor: GOLD, color: NAVY, background: `${GOLD}18` }}
              >
                {link.label}
              </button>
            ) : (
              <button
                key={link.href}
                onClick={() => handleNav(link.href)}
                className="text-sm font-medium text-foreground/70 hover:text-primary transition-colors relative group"
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-accent group-hover:w-full transition-all duration-300" />
              </button>
            )
          ))}
          <LanguageSwitcher />
          <button
            onClick={() => handleNav("#contact")}
            className="ml-1 px-5 py-2 text-sm font-semibold bg-primary text-white hover:bg-primary/90 transition-colors shadow-sm"
          >
            {t.nav_cta}
          </button>
        </div>

        {/* Mobile Toggle */}
        <button
          className="md:hidden p-2 text-foreground"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-white border-t border-border shadow-lg"
          >
            <div className="flex flex-col px-6 py-4 gap-3">
              {navLinks.map((link) => (
                link.isJoin ? (
                  <button
                    key={link.href}
                    onClick={() => { setOpen(false); navigate(link.href); }}
                    className="text-left text-base font-semibold py-1.5 px-3 border transition-colors"
                    style={{ borderColor: GOLD, color: NAVY, background: `${GOLD}18` }}
                  >
                    {link.label}
                  </button>
                ) : (
                  <button
                    key={link.href}
                    onClick={() => handleNav(link.href)}
                    className="text-left text-base font-medium text-foreground/70 hover:text-primary transition-colors py-1"
                  >
                    {link.label}
                  </button>
                )
              ))}
              <button
                onClick={() => handleNav("#contact")}
                className="mt-1 px-5 py-2.5 text-sm font-semibold bg-primary text-white hover:bg-primary/90 transition-colors"
              >
                {t.nav_cta}
              </button>
              {/* Mobile language switcher — row of 5 buttons */}
              <div className="flex gap-1.5 flex-wrap pt-1 border-t border-border mt-1">
                {LANG_OPTIONS.map((opt) => (
                  <button
                    key={opt.code}
                    onClick={() => { setLang(opt.code); setOpen(false); }}
                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold border transition-colors"
                    style={{
                      borderColor: lang === opt.code ? GOLD : "rgba(11,48,142,0.2)",
                      color: lang === opt.code ? GOLD : NAVY,
                      background: lang === opt.code ? `${GOLD}14` : "transparent",
                      fontWeight: lang === opt.code ? 700 : 500,
                    }}
                  >
                    <span>{opt.flag}</span>
                    <span>{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
